import type { ChatCitation, ChatErrorCode, LocalizedBotContent, PublicBotConfig } from '@apaxir/shared';
import { resolveBotContent } from '@apaxir/shared';
import { fetchPublicBotConfig } from './api/bot-config-client';
import { sendMessage } from './api/chat-client';
import { getTurnstileToken } from './api/turnstile';
import type { Translations } from './i18n/translations';
import { resolveLocale, translations } from './i18n/translations';
import {
  clearConversation,
  getConversationId,
  getOrCreateVisitorId,
  setConversationId,
} from './state/session';
import { styles } from './ui/styles';
import {
  bubbleHtml,
  citationsFooterHtml,
  errorStateHtml,
  escapeHtml,
  launcherHtml,
  panelHtml,
  suggestionsContainerHtml,
  typingIndicatorHtml,
} from './ui/templates';

type UiMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string; citations?: ChatCitation[] }
  | { role: 'error'; content: string };

/**
 * Widget entry component.
 *
 * Lifecycle on mount:
 *   1. Fetch PublicBotConfig via bot-id attribute
 *   2. Null → render error-state (bot not found / network error)
 *   3. resolveBotContent(config, requestedLocale) picks content + locale
 *      with fallback chain
 *   4. Null → render error-state (bot has no content)
 *   5. Otherwise: apply primaryColor + position, render launcher + panel
 *
 * Locale consistency: whatever locale resolveBotContent returns drives BOTH
 * this.botContent AND this.t — the UI never shows mixed localization.
 */
// Phase 3f.1 — fallback when the bot config doesn't ship maxMessageChars
// (e.g., legacy KV blob written before the migration). 500 mirrors the
// worker-side DEFAULT_MAX_MESSAGE_CHARS.
const DEFAULT_MAX_MESSAGE_CHARS = 500;

export class ApaxirChatbot extends HTMLElement {
  private root: ShadowRoot;
  private t!: Translations;
  private botContent: LocalizedBotContent | null = null;
  private botLogoUrl: string | null = null;
  private maxMessageChars = DEFAULT_MAX_MESSAGE_CHARS;
  // Phase 3f.3 — Turnstile config copied from PublicBotConfig at mount.
  // Token generation runs lazily on first message of a new conversation;
  // the hidden render host lives in the host page's light DOM (managed
  // by `api/turnstile.ts`).
  private turnstileEnabled = false;
  private turnstileSiteKey: string | null = null;
  private messagesEl: HTMLDivElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private sendBtn: HTMLButtonElement | null = null;
  private charCounterEl: HTMLDivElement | null = null;
  private uiMessages: UiMessage[] = [];
  private suggestionsDismissed = false;
  private isStreaming = false;
  private currentAssistantEl: HTMLDivElement | null = null;
  private abortController: AbortController | null = null;

  static get observedAttributes(): string[] {
    return ['bot-id', 'position', 'lang'];
  }

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    // Start with en UI chrome so any error state rendered before fetch
    // settles still has localized text available.
    this.t = translations.en;
  }

  async connectedCallback(): Promise<void> {
    this.setAttribute('data-open', 'false');

    const botId = this.botIdAttr();
    if (!botId) {
      this.renderErrorState(this.t.errorBotNotFound);
      return;
    }

    const requestedLocale = resolveLocale(this.getAttribute('lang'));
    // Show chrome in the requested locale immediately so a failed fetch
    // still surfaces a localized error.
    this.t = translations[requestedLocale];

    const config = await fetchPublicBotConfig(botId, this.getAttribute('data-cache-bust'));
    if (!config) {
      this.renderErrorState(this.t.errorBotNotFound);
      return;
    }

    const resolved = resolveBotContent(config, requestedLocale);
    if (!resolved) {
      this.renderErrorState(this.t.errorConfigMissing);
      return;
    }

    // Sync UI chrome to the locale actually used by bot content.
    this.botContent = resolved.content;
    this.botLogoUrl = config.logoUrl?.trim() || null;
    this.t = translations[resolved.locale];
    // Phase 3f.1 — pick up the per-bot char cap (default 500). Drives the
    // textarea maxlength + live counter; server enforces the same.
    this.maxMessageChars = config.maxMessageChars ?? DEFAULT_MAX_MESSAGE_CHARS;
    // Phase 3f.3 — Turnstile is opt-in per bot. Without siteKey we
    // treat it as off (server will reject anyway, but the widget never
    // tries to load CF script for a misconfigured bot).
    this.turnstileEnabled = !!config.turnstileEnabled && !!config.turnstileSiteKey;
    this.turnstileSiteKey = config.turnstileSiteKey ?? null;

    this.applyConfig(config);
    this.render();
  }

  disconnectedCallback(): void {
    this.abortController?.abort();
  }

  attributeChangedCallback(): void {
    // Phase 1: no runtime re-render on attribute change. Host page reloads
    // to change locale/position/bot-id.
  }

  // ----- public API -----

  open(): void {
    if (!this.botContent) return;
    this.setAttribute('data-open', 'true');
    setTimeout(() => this.inputEl?.focus(), 150);
  }

  close(): void {
    this.setAttribute('data-open', 'false');
  }

  toggle(): void {
    if (this.getAttribute('data-open') === 'true') this.close();
    else this.open();
  }

  resetConversation(): void {
    const botId = this.botIdAttr();
    if (!botId || !this.botContent) return;
    // eslint-disable-next-line no-alert
    const ok = confirm(this.t.restartConfirm);
    if (!ok) return;
    this.abortController?.abort();
    this.isStreaming = false;
    clearConversation(botId);
    this.uiMessages = [];
    this.suggestionsDismissed = false;
    this.currentAssistantEl = null;
    this.renderMessages();
    if (this.inputEl) this.inputEl.disabled = false;
    if (this.sendBtn) this.sendBtn.disabled = false;
    this.inputEl?.focus();
  }

  // ----- rendering -----

  private applyConfig(config: PublicBotConfig): void {
    // Primary color from bot config overrides CSS default.
    this.style.setProperty('--apaxir-primary', config.primaryColor);
    this.setAttribute('position', config.position);
  }

  private render(): void {
    if (!this.botContent) return;
    this.root.innerHTML = `<style>${styles}</style>${launcherHtml(this.t)}${panelHtml(this.t, this.botContent, this.botLogoUrl ?? undefined, this.maxMessageChars)}`;

    this.messagesEl = this.root.querySelector('.messages') as HTMLDivElement;
    this.inputEl = this.root.querySelector('.input') as HTMLTextAreaElement;
    this.sendBtn = this.root.querySelector('[data-action="send"]') as HTMLButtonElement;
    this.charCounterEl = this.root.querySelector('.char-counter') as HTMLDivElement | null;

    this.wireEvents();
    this.renderMessages();
    // Initialize counter state — sets send button disabled while empty.
    this.updateCharCounter();
  }

  private renderErrorState(message: string): void {
    this.root.innerHTML = `<style>${styles}</style>${errorStateHtml(message)}`;
    this.messagesEl = null;
    this.inputEl = null;
    this.sendBtn = null;
  }

  private renderMessages(): void {
    if (!this.messagesEl || !this.botContent) return;
    const parts: string[] = [];

    // Welcome + suggestions when conversation is fresh.
    if (this.uiMessages.length === 0) {
      parts.push(bubbleHtml('assistant', this.botContent.welcomeMessage));
      if (!this.suggestionsDismissed && this.botContent.suggestedPrompts.length) {
        parts.push(suggestionsContainerHtml(this.botContent.suggestedPrompts));
      }
    }

    for (const m of this.uiMessages) {
      if (m.role === 'assistant') {
        parts.push(bubbleHtml('assistant', m.content, m.citations, this.t));
      } else {
        parts.push(bubbleHtml(m.role, m.content));
      }
    }

    this.messagesEl.innerHTML = parts.join('');
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    if (this.messagesEl) this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // ----- events -----

  private wireEvents(): void {
    const launcher = this.root.querySelector('.launcher') as HTMLButtonElement;
    launcher.addEventListener('click', () => this.toggle());

    this.root.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-action]') as HTMLElement | null;
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === 'close') this.close();
      else if (action === 'restart') this.resetConversation();
      else if (action === 'send') this.handleSendClick();
      else if (action === 'suggestion') {
        const prompt = btn.dataset.prompt;
        if (prompt) this.sendUserMessage(prompt);
      }
    });

    this.inputEl?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSendClick();
      }
    });

    this.inputEl?.addEventListener('input', () => {
      if (!this.inputEl) return;
      this.inputEl.style.height = 'auto';
      this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + 'px';
      this.updateCharCounter();
    });
  }

  /**
   * Phase 3f.1 — sync the live char counter with the textarea. Hidden
   * until the user crosses ~50% of the limit so short messages don't see
   * chrome they don't need. Amber at 80%, red at the cap. Send button is
   * disabled while over (server still validates — UX courtesy only).
   */
  private updateCharCounter(): void {
    if (!this.inputEl || !this.charCounterEl) return;
    const len = this.inputEl.value.length;
    const max = this.maxMessageChars;
    const currentEl = this.charCounterEl.querySelector('.char-counter__current');
    if (currentEl) currentEl.textContent = String(len);

    const pct = max > 0 ? len / max : 0;
    if (pct >= 0.5) this.charCounterEl.classList.add('visible');
    else this.charCounterEl.classList.remove('visible');

    let state = 'ok';
    if (len > max) state = 'over';
    else if (pct >= 0.8) state = 'warn';
    this.charCounterEl.setAttribute('data-state', state);

    // Block send when over. Streaming-disabled state is handled separately;
    // when streaming AND over, both want disabled, no conflict.
    if (this.sendBtn && !this.isStreaming) {
      this.sendBtn.disabled = len > max || len === 0;
    }
  }

  private handleSendClick(): void {
    if (!this.inputEl) return;
    const text = this.inputEl.value.trim();
    if (!text || this.isStreaming) return;
    // Phase 3f.1 — client-side block past the limit. Server enforces the
    // same; this is UX only. Don't clear input — user should see what
    // they tried to send so they can trim it.
    if (text.length > this.maxMessageChars) return;
    this.inputEl.value = '';
    this.inputEl.style.height = 'auto';
    this.updateCharCounter();
    this.sendUserMessage(text);
  }

  // ----- chat flow -----

  private async sendUserMessage(text: string): Promise<void> {
    const botId = this.botIdAttr();
    if (!botId || !this.messagesEl || !this.botContent) return;
    if (this.isStreaming) return;

    this.uiMessages.push({ role: 'user', content: text });
    this.suggestionsDismissed = true;
    this.renderMessages();

    const assistantEl = document.createElement('div');
    assistantEl.className = 'bubble bubble--assistant';
    assistantEl.innerHTML = typingIndicatorHtml(this.t);
    this.messagesEl.appendChild(assistantEl);
    this.currentAssistantEl = assistantEl;
    this.scrollToBottom();

    this.setStreaming(true);
    this.abortController = new AbortController();

    // Phase 3f.3 — fetch a Turnstile token IF this is the first message
    // of a new conversation AND the bot has Turnstile enabled. Failure
    // here is fatal: we don't try to send without a token (server would
    // reject anyway — better to fail fast and let the user retry).
    const existingConversationId = getConversationId(botId) ?? undefined;
    let cfTurnstileToken: string | undefined;
    if (this.turnstileEnabled && this.turnstileSiteKey && !existingConversationId) {
      try {
        cfTurnstileToken = await getTurnstileToken({ sitekey: this.turnstileSiteKey });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[apaxir-chatbot] turnstile', err);
        assistantEl.remove();
        this.currentAssistantEl = null;
        this.uiMessages.push({ role: 'error', content: this.t.errorTurnstileFailed });
        this.renderMessages();
        this.setStreaming(false);
        this.abortController = null;
        return;
      }
    }

    let accumulated = '';
    let citations: ChatCitation[] | undefined;
    let sawToken = false;
    try {
      for await (const evt of sendMessage({
        botId,
        visitorId: getOrCreateVisitorId(),
        conversationId: existingConversationId,
        message: text,
        signal: this.abortController.signal,
        cfTurnstileToken,
      })) {
        if (evt.type === 'token') {
          if (!sawToken) {
            assistantEl.textContent = '';
            sawToken = true;
          }
          accumulated += evt.content;
          assistantEl.textContent = accumulated;
          this.scrollToBottom();
        } else if (evt.type === 'citations') {
          // Phase 3d.1 — append the citations footer to the live bubble
          // before `done` lands so users see attribution immediately.
          citations = evt.citations;
          if (citations.length > 0) {
            // Replace bubble innards: escaped text + footer. Doing this in
            // one innerHTML write keeps the assistantEl reference stable.
            assistantEl.innerHTML =
              escapeHtml(accumulated) + citationsFooterHtml(citations, this.t);
            this.scrollToBottom();
          }
        } else if (evt.type === 'done') {
          setConversationId(botId, evt.conversationId);
          this.uiMessages.push({ role: 'assistant', content: accumulated, citations });
          this.currentAssistantEl = null;
        } else if (evt.type === 'error') {
          assistantEl.remove();
          this.currentAssistantEl = null;
          this.uiMessages.push({ role: 'error', content: this.formatError(evt) });
          this.renderMessages();
        }
      }
    } catch (err) {
      assistantEl.remove();
      this.currentAssistantEl = null;
      // eslint-disable-next-line no-console
      console.error('[apaxir-chatbot]', err);
      this.uiMessages.push({ role: 'error', content: this.t.errorGeneric });
      this.renderMessages();
    } finally {
      this.setStreaming(false);
      this.abortController = null;
    }
  }

  private formatError(evt: {
    message: string;
    code?: ChatErrorCode;
    retryAfterSec?: number;
  }): string {
    // Phase 3e codes (`invalid_api_key`, `provider_error`, `provider_timeout`)
    // intentionally fall through to `errorGeneric` — widget strings stay
    // provider-agnostic. Specific copy can be added later if needed.
    if (evt.code === 'rate_limited' && typeof evt.retryAfterSec === 'number') {
      return this.t.errorRateLimited(evt.retryAfterSec);
    }
    if (evt.code === 'no_api_key_configured') {
      return this.t.errorNoApiKey;
    }
    if (evt.code === 'monthly_cost_cap_exceeded') {
      return this.t.errorMonthlyCapExceeded;
    }
    // Phase 3f.1 — abuse-prevention codes.
    if (evt.code === 'message_too_long') {
      return this.t.errorMessageTooLong(this.maxMessageChars);
    }
    if (evt.code === 'conversation_limit_reached') {
      return this.t.errorConversationLimitReached;
    }
    // Phase 3f.2 — daily quota + cap-disable.
    if (evt.code === 'daily_quota_exceeded') {
      return this.t.errorDailyQuotaExceeded;
    }
    if (evt.code === 'bot_temporarily_unavailable') {
      return this.t.errorBotTemporarilyUnavailable;
    }
    // Phase 3f.3 — Turnstile.
    if (evt.code === 'turnstile_failed') {
      return this.t.errorTurnstileFailed;
    }
    return this.t.errorGeneric;
  }

  private setStreaming(streaming: boolean): void {
    this.isStreaming = streaming;
    if (this.inputEl) this.inputEl.disabled = streaming;
    if (this.sendBtn) this.sendBtn.disabled = streaming;
    // When streaming ends, re-evaluate send button via counter logic so
    // empty input keeps it disabled instead of bouncing back to enabled.
    if (!streaming) this.updateCharCounter();
  }

  private botIdAttr(): string | null {
    const id = this.getAttribute('bot-id');
    return id && id.trim() ? id.trim() : null;
  }
}

export { escapeHtml };

if (!customElements.get('apaxir-chatbot')) {
  customElements.define('apaxir-chatbot', ApaxirChatbot);
}
